import { useState } from 'react';
import { PRICING, getUnitPrice, type Gender, type DeliveryWeeks } from '../utils/pricing';
import './PriceSimulator.css';

interface Props {
  gender: Gender;
  onClose: () => void;
}

const DELIVERY_OPTIONS: { weeks: DeliveryWeeks; label: string }[] = [
  { weeks: '8mie', label: '8 semaines Made In Europe' },
  { weeks: 8,      label: '8 semaines'                },
  { weeks: 14,     label: '14 semaines'               },
  { weeks: 16,     label: '16 semaines'               },
];

export default function PriceSimulator({ gender, onClose }: Props) {
  const [activeGender, setActiveGender] = useState<Gender>(gender);
  const [weeks, setWeeks] = useState<DeliveryWeeks>(16);
  const [qty, setQty]     = useState<number>(30);
  const [raw, setRaw]     = useState<string>('30');

  const pricing   = PRICING[activeGender];
  const delivery  = pricing.deliveries.find(d => d.weeks === weeks)!;
  const unitPrice = getUnitPrice(gender, weeks, qty);
  const total     = unitPrice != null ? unitPrice * qty : null;
  const minQty    = Math.min(...delivery.tiers.map(t => t.minQty));

  const handleQty = (val: string) => {
    setRaw(val);
    const n = parseInt(val, 10);
    if (!isNaN(n) && n > 0) setQty(n);
  };

  return (
    <div className="ps-overlay" role="dialog" aria-modal="true" aria-label="Simulateur de tarif">
      <div className="ps-backdrop" onClick={onClose} />
      <div className="ps-card">

        {/* Header */}
        <div className="ps-header">
          <div>
            <h2 className="ps-title">Simulateur de tarif</h2>
            <p className="ps-subtitle">
              Modèle 100&nbsp;g/m² — Réf.&nbsp;<strong>{pricing.ref}</strong>
            </p>
            <div className="ps-gender-toggle">
              <button
                type="button"
                className={`ps-gender-btn ps-gender-btn--homme${activeGender === 'homme' ? ' is-active' : ''}`}
                onClick={() => setActiveGender('homme')}
              >
                ♂ Homme
              </button>
              <button
                type="button"
                className={`ps-gender-btn ps-gender-btn--femme${activeGender === 'femme' ? ' is-active' : ''}`}
                onClick={() => setActiveGender('femme')}
              >
                ♀ Femme
              </button>
            </div>
          </div>
          <button className="ps-close" type="button" onClick={onClose} aria-label="Fermer">×</button>
        </div>

        {/* Body — 2 colonnes */}
        <div className="ps-body">

          {/* Colonne gauche : contrôles */}
          <div className="ps-controls">
            <div>
              <label className="ps-label">Délai</label>
              <div className="ps-delivery-tabs">
                {DELIVERY_OPTIONS.map(opt => (
                  <button
                    key={opt.weeks}
                    type="button"
                    className={`ps-delivery-tab ${weeks === opt.weeks ? 'is-active' : ''}`}
                    onClick={() => setWeeks(opt.weeks)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ps-qty-row">
              <label className="ps-label" htmlFor="ps-qty">Quantité</label>
              <div className="ps-qty-input-wrap">
                <input
                  id="ps-qty"
                  type="number"
                  min={1}
                  className="ps-qty-input"
                  value={raw}
                  onChange={e => handleQty(e.target.value)}
                  onBlur={() => setRaw(String(qty))}
                />
                <span className="ps-qty-unit">unités</span>
              </div>
              {qty < minQty && (
                <p className="ps-qty-warn">Min.&nbsp;{minQty}&nbsp;u.</p>
              )}
            </div>
          </div>

          {/* Colonne droite : table + résultat */}
          <div className="ps-right">
            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Quantité</th>
                    <th>Prix unit. HT</th>
                    <th>Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {delivery.tiers.map((tier, i) => {
                    const isActive = qty >= tier.minQty &&
                      (i === delivery.tiers.length - 1 || qty < delivery.tiers[i + 1].minQty);
                    return (
                      <tr key={tier.minQty} className={isActive ? 'is-active' : ''}>
                        <td>{tier.minQty.toLocaleString('fr-FR')}&nbsp;+</td>
                        <td className="ps-price">{tier.price.toFixed(2)}&nbsp;€</td>
                        <td className="ps-total">
                          {isActive && total != null
                            ? total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                            : '≥ ' + (tier.price * tier.minQty).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €'
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="ps-result">
              {unitPrice != null && total != null ? (
                <>
                  <div className="ps-result-row">
                    <span className="ps-result-label">Prix unitaire HT</span>
                    <span className="ps-result-value">{unitPrice.toFixed(2)}&nbsp;€</span>
                  </div>
                  <div className="ps-result-row ps-result-row--total">
                    <span className="ps-result-label">Total HT ({qty.toLocaleString('fr-FR')} unités)</span>
                    <span className="ps-result-value ps-result-value--total">
                      {total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}&nbsp;€
                    </span>
                  </div>
                </>
              ) : (
                <p className="ps-result-na">Min.&nbsp;{minQty}&nbsp;unités pour ce délai</p>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
